import { useState, useEffect, type FC } from 'react';
import { Link, useLocation } from 'react-router-dom';
import {
    LuMenu, LuX, LuChevronDown, LuSun, LuMoon, LuLogOut, LuRocket, LuCircleHelp, LuCreditCard,
    LuSearch, LuHeadset, LuSmartphone,
} from 'react-icons/lu';
import { getUserPermissions, hasPermission, PERMISSOES } from '../config/permissions';
import { GRUPOS_OPERACAO, itensVisiveis, type ItemNav } from '../config/navegacao';
import { gruposConfigVisiveis, rotaCasaCom, ehRotaDeConfiguracao, ehSubmenu } from '../config/configuracoes';
import { useGlobalNotification } from '../contexts/GlobalNotificationContext';
import { usePrimeirosPassos } from '../contexts/PrimeirosPassosContext';
import { useTheme } from '../contexts/ThemeContext';
import NotificationsBell from './NotificationsBell';
import { abrirBuscaGlobal } from './BuscaGlobal';
import BrikMark from './BrikMark';
import AgentePorta from './AgentePorta';
import SuporteModal from './SuporteModal';
import { rodandoInstalado } from '../lib/instalarApp';

/**
 * A navegação do celular: o cabeçalho fixo e a gaveta.
 *
 * Substituiu a gaveta escrita à mão dentro do `MainLayout`, que tinha sete
 * destinos fixos enquanto a sidebar tinha vinte e o trilho de Configurações,
 * dezoito — quem entrava pelo telefone não alcançava Contatos, Tarefas,
 * Relatórios nem nenhuma tela de configuração. Agora as duas listas vêm das
 * mesmas fontes da sidebar e do trilho (`config/navegacao.ts` e
 * `config/configuracoes.ts`), então tela nova aparece aqui sozinha.
 *
 * Mora fora do `MainLayout` porque precisa estar DENTRO do
 * `GlobalNotificationProvider` para o sino ter os números.
 */

interface Props {
    onLogout: () => void;
}

const MobileNav: FC<Props> = ({ onLogout }) => {
    const [aberta, setAberta] = useState(false);
    const [configAberto, setConfigAberto] = useState(false);
    const [suporteAberto, setSuporteAberto] = useState(false);
    const location = useLocation();
    const { resumo, recarregarResumo } = useGlobalNotification();
    const { theme, toggleTheme } = useTheme();
    const {
        concluidos: passosConcluidos, total: passosTotal, carregando: passosCarregando,
    } = usePrimeirosPassos();

    /* Trocar de tela fecha a gaveta — senão ela cobre a tela que a pessoa
       acabou de escolher. */
    useEffect(() => { setAberta(false); }, [location.pathname]);

    /* Quem entra numa tela de configuração encontra a seção já aberta na
       próxima vez que puxar a gaveta. */
    useEffect(() => {
        if (ehRotaDeConfiguracao(location.pathname)) setConfigAberto(true);
    }, [location.pathname]);

    /* Gaveta aberta trava a rolagem do conteúdo atrás dela. */
    useEffect(() => {
        if (!aberta) return;
        const antes = document.body.style.overflow;
        document.body.style.overflow = 'hidden';
        return () => { document.body.style.overflow = antes; };
    }, [aberta]);

    const permissoes = getUserPermissions();
    let empresa4Ou8 = false;
    try {
        const u = JSON.parse(localStorage.getItem('user') || '{}');
        empresa4Ou8 = u?.empresaId === 4 || u?.empresaId === 8;
    } catch { /* ignora */ }

    const gruposOperacao = GRUPOS_OPERACAO
        .map(g => ({ ...g, itens: itensVisiveis(g, permissoes) }))
        .filter(g => g.itens.length > 0);
    const gruposConfig = gruposConfigVisiveis(permissoes, empresa4Ou8, hasPermission);

    const mostrarPassos = !passosCarregando && passosTotal > 0 && passosConcluidos < passosTotal;

    const item = (i: ItemNav) => {
        const Icone = i.icone;
        const n = i.badge === 'tarefasVencidas' ? resumo.tarefasVencidas
            : i.badge === 'mensagensNaoLidas' ? resumo.mensagensNaoLidas
            : 0;
        return (
            <Link
                key={i.to}
                to={i.to}
                className={`drawer-link ${rotaCasaCom(location.pathname, i.to) ? 'active' : ''}`}
            >
                <Icone />
                <span className="drawer-link-txt">{i.rotulo}</span>
                {n > 0 && <span className="drawer-badge">{n > 99 ? '99+' : n}</span>}
            </Link>
        );
    };

    return (
        <>
            {/* A sidebar some no celular; sobra este cabeçalho com a gaveta, a
                marca e o sino — que até aqui só existia dentro da sidebar. */}
            <header className="mobile-header">
                <button
                    className="mobile-header-btn"
                    onClick={() => setAberta(v => !v)}
                    aria-label="Menu"
                    aria-expanded={aberta}
                >
                    <LuMenu />
                </button>
                <Link to="/home" className="mobile-header-brand" aria-label="Início">
                    <BrikMark />
                </Link>
                {/* A lupa: até 2026-10-01 a busca morava dentro da sidebar, que
                    não existe no celular — quem entrava pelo telefone não tinha
                    busca nenhuma. Abre a mesma paleta do Ctrl+K. */}
                <button
                    type="button"
                    className="mobile-header-btn mobile-header-busca"
                    onClick={abrirBuscaGlobal}
                    aria-label="Buscar"
                >
                    <LuSearch />
                </button>
                {/* A porta do agente: no desktop ela é a última ação do
                    cabeçalho de cada tela; no celular mora aqui, fixa. */}
                <AgentePorta variante="celular" />
                <div className="mobile-header-sino">
                    <NotificationsBell naoLidos={resumo.avisosNaoLidos} onContagemMudou={recarregarResumo} />
                </div>
            </header>

            <div
                className={`mobile-overlay ${aberta ? 'visible' : ''}`}
                onClick={() => setAberta(false)}
            />

            <div className={`mobile-drawer ${aberta ? 'open' : ''}`}>
                <div className="drawer-header">
                    <h3>Navegação</h3>
                    <button className="close-drawer" onClick={() => setAberta(false)} aria-label="Fechar">
                        <LuX size={18} />
                    </button>
                </div>

                <nav className="drawer-nav">
                    {gruposOperacao.map(g => (
                        <div className="drawer-grupo" key={g.titulo ?? 'principal'}>
                            {g.titulo && <span className="drawer-grupo-tit">{g.titulo}</span>}
                            {g.itens.map(item)}
                        </div>
                    ))}

                    {gruposConfig.length > 0 && (
                        <div className="drawer-grupo">
                            {/* Recolhida: aberta, os dezoito itens empurrariam a
                                operação para fora da primeira dobra. */}
                            <button
                                type="button"
                                className={`drawer-sec ${configAberto ? 'is-open' : ''}`}
                                onClick={() => setConfigAberto(v => !v)}
                                aria-expanded={configAberto}
                            >
                                <LuChevronDown className="drawer-sec-chev" />
                                <span>Configurações</span>
                            </button>
                            {configAberto && (
                                <div className="drawer-sub">
                                    {gruposConfig.map(g => (
                                        <div className="drawer-sub-grupo" key={g.titulo}>
                                            <span className="drawer-grupo-tit">{g.titulo}</span>
                                            {g.itens.map(e => ehSubmenu(e) ? (
                                                /* Submenu (Sistema › Relatórios): rótulo sem tela,
                                                   itens um degrau mais para dentro. */
                                                <div className="drawer-submenu" key={e.rotulo}>
                                                    <span className="drawer-submenu-tit">{e.rotulo}</span>
                                                    {e.itens.map(i => (
                                                        <Link
                                                            key={i.to}
                                                            to={i.to}
                                                            className={`drawer-link is-sub is-sub2 ${rotaCasaCom(location.pathname, i.to) ? 'active' : ''}`}
                                                        >
                                                            <span className="drawer-link-txt">{i.rotulo}</span>
                                                        </Link>
                                                    ))}
                                                </div>
                                            ) : (
                                                <Link
                                                    key={e.to}
                                                    to={e.to}
                                                    className={`drawer-link is-sub ${rotaCasaCom(location.pathname, e.to) ? 'active' : ''}`}
                                                >
                                                    <span className="drawer-link-txt">{e.rotulo}</span>
                                                </Link>
                                            ))}
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>
                    )}

                    <div className="drawer-foot">
                        {mostrarPassos && (
                            <Link to="/primeiros-passos" className={`drawer-link ${location.pathname === '/primeiros-passos' ? 'active' : ''}`}>
                                <LuRocket />
                                <span className="drawer-link-txt">Primeiros passos</span>
                                <span className="drawer-badge is-calmo">{passosConcluidos}/{passosTotal}</span>
                            </Link>
                        )}
                        {!empresa4Ou8 && hasPermission(permissoes, [PERMISSOES.plano]) && (
                            <Link to="/plano" className={`drawer-link ${location.pathname === '/plano' ? 'active' : ''}`}>
                                <LuCreditCard />
                                <span className="drawer-link-txt">Meu plano</span>
                            </Link>
                        )}
                        <Link to="/help" className={`drawer-link ${rotaCasaCom(location.pathname, '/help') ? 'active' : ''}`}>
                            <LuCircleHelp />
                            <span className="drawer-link-txt">Central de ajuda</span>
                        </Link>
                        {/* No celular não há QR: o próprio aparelho vai ao passo a passo.
                            Aberto pelo ícone, já está instalado e o item sai. */}
                        {!rodandoInstalado() && (
                            <Link to="/instalar" className="drawer-link">
                                <LuSmartphone />
                                <span className="drawer-link-txt">Instalar o app</span>
                            </Link>
                        )}
                        {/* A gaveta fecha antes: a modal não abre por cima de uma gaveta aberta. */}
                        <button type="button" className="drawer-link is-botao"
                            onClick={() => { setAberta(false); setSuporteAberto(true); }}>
                            <LuHeadset />
                            <span className="drawer-link-txt">Suporte e sugestões</span>
                        </button>
                        <button type="button" className="drawer-link is-botao" onClick={toggleTheme}>
                            {theme === 'dark' ? <LuSun /> : <LuMoon />}
                            <span className="drawer-link-txt">{theme === 'dark' ? 'Modo claro' : 'Modo escuro'}</span>
                        </button>
                        <button type="button" className="drawer-link is-botao is-sair" onClick={onLogout}>
                            <LuLogOut />
                            <span className="drawer-link-txt">Sair</span>
                        </button>
                    </div>
                </nav>
            </div>
            <SuporteModal open={suporteAberto} onClose={() => setSuporteAberto(false)} />
        </>
    );
};

export default MobileNav;
